/**
 * API client for map markers endpoint.
 * Handles bounds-based marker fetching with pagination and error handling.
 */

import type {
  Activity,
  Country,
  FetchGeocodeForwardOptions,
  FetchLocationSearchOptions,
  FetchMapMarkersOptions,
  FetchMapPhotosOptions,
  GeocodeForwardResponse,
  MapBounds,
  MapMarkersResponse,
  MapPhotosResponse,
  Organization,
  Province,
  UserPlaceApiRow,
  UserPlacesMapResponse,
  UserPlacesSearchResponse,
  CreateUserPlacePayload,
  UpdateUserPlacePayload,
  CopyUserPlacePayload,
  UserPlaceUsagesResponse,
} from '../types'
import { normalizeBounds } from '../utils/normalizeBounds'
import { appendMarkerAdvancedParams } from '../utils/markerFilterParams'
import { getApiBaseUrl, getApiVersion } from '../../../shared/api/runtimeConfig'
import { clearTokensIfUnauthorized } from '../../auth/utils/tokenStorage'
import i18n from '../../../i18n'

const API_URL = getApiBaseUrl()
const API_VERSION = getApiVersion()
const MAX_PAGES = 3 // Hard cap for search; markers use MARKERS_MAX_PAGES
const MARKERS_MAX_PAGES = 1 // Carte : une seule page pour éviter de charger des milliers de marqueurs (ex. tout le Québec)
const DEFAULT_PAGE_SIZE = 1000
const SEARCH_MIN_LENGTH = 2 // Minimum query length for search
const DEFAULT_SEARCH_PAGE_SIZE = 200
const MAX_SEARCH_PAGES = 1 // Keep search snappy (<1s); fetch more pages on-demand later if needed

function extractErrorMessage(errorData: unknown): string | null {
  if (!errorData || typeof errorData !== 'object') return null

  const anyData = errorData as Record<string, unknown>

  if (typeof anyData.detail === 'string' && anyData.detail.trim()) {
    return anyData.detail
  }

  if (typeof anyData.message === 'string' && anyData.message.trim()) {
    return anyData.message
  }

  // DRF field errors: { "bounds_north": ["This field is required."] }
  for (const value of Object.values(anyData)) {
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
      return value.filter((v) => typeof v === 'string').join(', ')
    }
  }

  return null
}

export type MapApiError = Error & { status?: number; retryAfterSeconds?: number | null }

/** Délai DRF : en-tête Retry-After, sinon premier nombre du `detail` (« … in 1234 seconds »). */
function parseRetryAfterSeconds(response: Response, errorData: unknown): number | null {
  const header = Number.parseInt(response.headers.get('Retry-After') ?? '', 10)
  if (Number.isFinite(header) && header >= 0) return header
  const detail = (errorData as { detail?: unknown } | null)?.detail
  const match = typeof detail === 'string' ? detail.match(/\d+/) : null
  return match ? Number.parseInt(match[0], 10) : null
}

async function buildMapApiError(response: Response, fallbackMessage: string): Promise<MapApiError> {
  const errorData = await response.json().catch(() => ({}))
  const err = new Error(extractErrorMessage(errorData) || fallbackMessage) as MapApiError
  err.status = response.status
  if (response.status === 429) {
    err.retryAfterSeconds = parseRetryAfterSeconds(response, errorData)
  }
  return err
}

function isValidBounds(bounds: MapBounds): boolean {
  // Backend constraint (MVP): dateline-crossing bounds rejected (east must be > west)
  if (!(bounds.east > bounds.west)) return false
  if (!(bounds.north > bounds.south)) return false
  return true
}

function getAuthHeadersForMe(): HeadersInit {
  const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null
  const headers: HeadersInit = { Accept: 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

export interface UserPlaceContextFilter {
  itineraryId?: number | null
  kind?: 'poi' | 'accommodation' | null
  /** Filtre profil : global | itinerary (indépendant de itineraryId). */
  scope?: 'global' | 'itinerary' | null
}

function appendUserPlaceContextParams(params: URLSearchParams, filter?: UserPlaceContextFilter) {
  if (filter?.itineraryId != null && filter.itineraryId > 0) {
    params.set('itinerary_id', String(filter.itineraryId))
  }
  if (filter?.kind === 'poi' || filter?.kind === 'accommodation') {
    params.set('kind', filter.kind)
  }
  if (filter?.scope === 'global' || filter?.scope === 'itinerary') {
    params.set('scope', filter.scope)
  }
}

/**
 * Lieux personnels visibles dans la bbox (utilisateur connecté).
 * bounds : minLon,minLat,maxLon,maxLat (Mapbox), aligné sur normalizeBounds.
 */
export async function fetchUserPlacesMap(
  bounds: MapBounds,
  options: { signal?: AbortSignal } & UserPlaceContextFilter = {},
): Promise<UserPlacesMapResponse> {
  const normalizedBounds = normalizeBounds(bounds)
  if (!isValidBounds(normalizedBounds)) {
    return { count: 0, truncated: false, results: [] }
  }
  // Pas de token → pas d’appel (évite 401 spam après session vidée).
  const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null
  if (!token) {
    return { count: 0, truncated: false, results: [] }
  }
  const { west, south, east, north } = normalizedBounds
  const params = new URLSearchParams({
    bounds: `${west},${south},${east},${north}`,
  })
  appendUserPlaceContextParams(params, options)
  const url = `${API_URL}/${API_VERSION}/me/places/map/?${params.toString()}`
  const response = await fetch(url, {
    method: 'GET',
    headers: { ...getAuthHeadersForMe(), Accept: 'application/json' },
    signal: options.signal,
    cache: 'no-store',
  })
  if (response.status === 401) {
    clearTokensIfUnauthorized()
    return { count: 0, truncated: false, results: [] }
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(msg || `me/places/map: ${response.status}`)
  }
  return (await response.json()) as UserPlacesMapResponse
}

/**
 * Fetches map markers within the specified bounds.
 * Automatically handles pagination up to a hard cap to prevent excessive loads.
 */
export async function fetchMapMarkers(
  bounds: MapBounds,
  options: FetchMapMarkersOptions = {},
): Promise<MapMarkersResponse> {
  // Normalize bounds for cache friendliness
  const normalizedBounds = normalizeBounds(bounds)

  // Avoid server-side 400s for invalid bounds (e.g., date line crossing)
  if (!isValidBounds(normalizedBounds)) {
    return {
      count: 0,
      next: null,
      previous: null,
      results: [],
    }
  }

  // Build base URL
  const baseUrl = `${API_URL}/${API_VERSION}/locations/markers/`

  // Build query parameters
  const params = new URLSearchParams({
    bounds_north: normalizedBounds.north.toString(),
    bounds_south: normalizedBounds.south.toString(),
    bounds_east: normalizedBounds.east.toString(),
    bounds_west: normalizedBounds.west.toString(),
  })

  if (options.cursor) {
    params.append('cursor', options.cursor)
  }

  const pageSize = options.pageSize || DEFAULT_PAGE_SIZE
  params.append('page_size', pageSize.toString())
  
  if (options.season) {
    params.append('season', options.season)
  }
  
  if (options.country_id) {
    params.append('country_id', options.country_id.toString())
  }
  if (options.province_id) {
    params.append('province_id', options.province_id.toString())
  }
  if (options.activity_id) {
    params.append('activity_id', options.activity_id.toString())
  }
  if (options.organization_id) {
    params.append('organization_id', options.organization_id.toString())
  }
  if (options.location_types && options.location_types.length >= 0) {
    if (options.location_types.length === 0) {
      params.append('location_types', '')
    } else {
      options.location_types.forEach((t) => params.append('location_types', t))
    }
  }
  if (options.poi_type_slugs?.length) {
    options.poi_type_slugs.forEach((s) => params.append('poi_type_slugs', s))
  }
  if (options.poi_category_slugs?.length) {
    options.poi_category_slugs.forEach((s) => params.append('poi_category_slugs', s))
  }
  if (options.accommodation_type_slugs?.length) {
    options.accommodation_type_slugs.forEach((s) => params.append('accommodation_type_slugs', s))
  }
  if (options.building_type_ids?.length) {
    options.building_type_ids.forEach((id) => params.append('building_type_ids', String(id)))
  }
  if (options.organization_type_ids?.length) {
    options.organization_type_ids.forEach((id) => params.append('organization_type_ids', String(id)))
  }
  if (options.camping_type_ids?.length) {
    options.camping_type_ids.forEach((id) => params.append('camping_type_ids', String(id)))
  }

  appendMarkerAdvancedParams(params, {
    country_id: options.country_id,
    province_id: options.province_id,
    organization_id: options.organization_id,
    activity_id: options.activity_id,
    activity_ids: options.activity_ids,
    refuge_activity_ids: options.refuge_activity_ids,
    organization_activity_ids: options.organization_activity_ids,
    camping_activity_ids: options.camping_activity_ids,
    capacity_min: options.capacity_min,
    capacity_max: options.capacity_max,
    dogs_allowed: options.dogs_allowed,
    car_access: options.car_access,
    drinking_water: options.drinking_water,
    toilet: options.toilet,
    rental_type: options.rental_type,
    exclusive_occupation: options.exclusive_occupation,
    dog_friendly: options.dog_friendly,
    showers: options.showers,
    welcome_cyclist: options.welcome_cyclist,
    tent_only: options.tent_only,
    verified: options.verified,
    placement_type_ids: options.placement_type_ids,
    lodging_type_id: options.lodging_type_id,
    contains_location_types: options.contains_location_types,
    contains_poi_type_ids: options.contains_poi_type_ids,
  })

  // Fetch first page — include auth headers so the backend knows the user is authenticated
  // (affects bounds limits and page_size caps for anonymous vs authenticated users)
  const url = `${baseUrl}?${params.toString()}`
  const response = await fetch(url, {
    signal: options.signal,
    headers: getAuthHeadersForMe(),
  })

  if (!response.ok) {
    if (response.status === 401) {
      clearTokensIfUnauthorized()
    }
    throw await buildMapApiError(
      response,
      i18n.t('map:mapApi.failed_to_fetch_markers', { status: response.status, statusText: response.statusText }),
    )
  }

  const data: MapMarkersResponse = await response.json()

  // Ne pas suivre la pagination pour la carte : une seule page (ex. 1000 marqueurs max).
  // Sinon on charge des milliers de marqueurs pour tout le Québec (refuge + camping = 2000+).
  // Quand l'utilisateur zoome/déplace, les bounds sont plus petites et une page suffit.
  return {
    ...data,
    results: data.results,
    next: null,
  }
}

const DEFAULT_PHOTOS_PAGE_SIZE = 500

/**
 * Photos publiques approuvées dans l’emprise carte (calque « Photos »).
 */
export async function fetchMapPhotos(
  bounds: MapBounds,
  options: FetchMapPhotosOptions = {},
): Promise<MapPhotosResponse> {
  const normalizedBounds = normalizeBounds(bounds)
  if (!isValidBounds(normalizedBounds)) {
    return { count: 0, results: [] }
  }

  const baseUrl = `${API_URL}/${API_VERSION}/photos/map/`
  const params = new URLSearchParams({
    bounds_north: normalizedBounds.north.toString(),
    bounds_south: normalizedBounds.south.toString(),
    bounds_east: normalizedBounds.east.toString(),
    bounds_west: normalizedBounds.west.toString(),
  })
  params.append('page_size', String(options.pageSize ?? DEFAULT_PHOTOS_PAGE_SIZE))

  const url = `${baseUrl}?${params.toString()}`
  const response = await fetch(url, { signal: options.signal, headers: getAuthHeadersForMe() })
  if (!response.ok) {
    if (response.status === 401) {
      clearTokensIfUnauthorized()
    }
    throw await buildMapApiError(
      response,
      i18n.t('map:mapApi.failed_to_fetch_map_photos', { status: response.status, statusText: response.statusText }),
    )
  }
  return response.json() as Promise<MapPhotosResponse>
}


/**
 * Fetches location search results based on query string.
 * Handles pagination and error handling similar to fetchMapMarkers.
 *
 * @param query - Search query string (minimum 2 characters)
 * @param options - Optional fetch options (cursor, pageSize, signal)
 * @returns Promise with search results response
 *
 * @example
 * ```ts
 * const results = await fetchLocationSearch('camping', { pageSize: 50 })
 * ```
 */
export async function fetchLocationSearch(
  query: string,
  options: FetchLocationSearchOptions = {},
): Promise<MapMarkersResponse> {
  // Normalize and validate query
  const normalizedQuery = query.trim()
  if (normalizedQuery.length < SEARCH_MIN_LENGTH) {
    return {
      count: 0,
      next: null,
      previous: null,
      results: [],
    }
  }

  // Build base URL
  const baseUrl = `${API_URL}/${API_VERSION}/locations/search/`

  // Build query parameters
  const params = new URLSearchParams({
    q: normalizedQuery,
  })

  if (options.cursor) {
    params.append('cursor', options.cursor)
  }

  const pageSize = options.pageSize || DEFAULT_SEARCH_PAGE_SIZE
  params.append('page_size', pageSize.toString())
  
  if (options.season) {
    params.append('season', options.season)
  }
  
  if (options.country_id) {
    params.append('country_id', options.country_id.toString())
  }
  if (options.province_id) {
    params.append('province_id', options.province_id.toString())
  }
  if (options.activity_id) {
    params.append('activity_id', options.activity_id.toString())
  }
  if (options.organization_id) {
    params.append('organization_id', options.organization_id.toString())
  }
  
  if (options.location_types && options.location_types.length > 0) {
    options.location_types.forEach(type => {
      params.append('location_types', type)
    })
  }

  if (options.bounds) {
    const { north, south, east, west } = options.bounds
    params.append('bounds_north', north.toString())
    params.append('bounds_south', south.toString())
    params.append('bounds_east', east.toString())
    params.append('bounds_west', west.toString())
  }

  // Fetch first page
  const url = `${baseUrl}?${params.toString()}`
  const response = await fetch(url, { signal: options.signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(
      msg || i18n.t('map:mapApi.failed_to_search_locations', { status: response.status, statusText: response.statusText }),
    )
  }

  const data: MapMarkersResponse = await response.json()

  // Une seule page demandée (ex. page de recherche avec pagination)
  if (options.singlePage) {
    return data
  }

  // If there's a next page and we haven't hit the cap, fetch additional pages
  let allResults = [...data.results]
  let nextCursor = data.next
  let pagesFetched = 1

  while (nextCursor && pagesFetched < MAX_SEARCH_PAGES) {
    // Extract cursor from next URL
    const nextUrl = new URL(nextCursor, baseUrl)
    const cursor = nextUrl.searchParams.get('cursor')
    if (!cursor) break

    const nextParams = new URLSearchParams({
      q: normalizedQuery,
      cursor,
      page_size: pageSize.toString(),
    })
    
    if (options.season) {
      nextParams.append('season', options.season)
    }
    
    if (options.country_id) {
      nextParams.append('country_id', options.country_id.toString())
    }
    if (options.province_id) {
      nextParams.append('province_id', options.province_id.toString())
    }
    if (options.activity_id) {
      nextParams.append('activity_id', options.activity_id.toString())
    }
    if (options.organization_id) {
      nextParams.append('organization_id', options.organization_id.toString())
    }

    const nextResponse = await fetch(`${baseUrl}?${nextParams.toString()}`, {
      signal: options.signal,
    })
    if (!nextResponse.ok) break

    const nextData: MapMarkersResponse = await nextResponse.json()
    allResults = [...allResults, ...nextData.results]
    nextCursor = nextData.next
    pagesFetched++
  }

  return {
    ...data,
    results: allResults,
    // Update next to null if we've fetched all pages or hit the cap
    next: pagesFetched >= MAX_SEARCH_PAGES && nextCursor ? nextCursor : null,
  }
}

/**
 * Forward geocode via backend proxy (Mapbox v6). No token in the browser.
 */
export async function fetchGeocodeForward(
  query: string,
  options: FetchGeocodeForwardOptions = {},
): Promise<GeocodeForwardResponse> {
  const q = query.trim()
  if (q.length < 5) {
    return { configured: false, results: [] }
  }
  const params = new URLSearchParams({ q })
  if (options.proximityLng != null && options.proximityLat != null) {
    params.append('proximity_lng', String(options.proximityLng))
    params.append('proximity_lat', String(options.proximityLat))
  }
  if (options.limit != null) {
    params.append('limit', String(options.limit))
  }
  const url = `${API_URL}/${API_VERSION}/geocode/forward/?${params.toString()}`
  const response = await fetch(url, { signal: options.signal })
  if (response.status === 429) {
    return {
      configured: true,
      results: [],
      error: 'geocode_rate_limited',
    }
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(msg || i18n.t('map:mapApi.geocode_failed', { status: response.status, statusText: response.statusText }))
  }
  return response.json() as Promise<GeocodeForwardResponse>
}

/**
 * Reverse geocode (Mapbox v6 via proxy).
 */
export async function fetchGeocodeReverse(
  latitude: number,
  longitude: number,
  options: { signal?: AbortSignal; limit?: number } = {},
): Promise<GeocodeForwardResponse> {
  const params = new URLSearchParams({
    longitude: String(longitude),
    latitude: String(latitude),
    limit: String(options.limit ?? 1),
  })
  const url = `${API_URL}/${API_VERSION}/geocode/reverse/?${params.toString()}`
  const response = await fetch(url, { signal: options.signal })
  if (response.status === 429) {
    return { configured: true, results: [], error: 'geocode_rate_limited' }
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(msg || i18n.t('map:mapApi.geocode_reverse_failed', { status: response.status }))
  }
  return response.json() as Promise<GeocodeForwardResponse>
}

export interface FetchUserPlacesSearchOptions extends UserPlaceContextFilter {
  q?: string
  page?: number
  pageSize?: number
  centerLat?: number | null
  centerLng?: number | null
  signal?: AbortSignal
}

/**
 * Recherche paginée des lieux personnels (GET /me/places/search/).
 */
export async function fetchUserPlacesSearch(
  options: FetchUserPlacesSearchOptions = {},
): Promise<UserPlacesSearchResponse> {
  const params = new URLSearchParams()
  if (options.q != null && options.q.trim()) params.set('q', options.q.trim())
  if (options.page != null) params.set('page', String(options.page))
  if (options.pageSize != null) params.set('page_size', String(options.pageSize))
  if (options.centerLat != null && options.centerLng != null) {
    params.set('center_lat', String(options.centerLat))
    params.set('center_lng', String(options.centerLng))
  }
  appendUserPlaceContextParams(params, options)
  const qs = params.toString()
  const url = `${API_URL}/${API_VERSION}/me/places/search/${qs ? `?${qs}` : ''}`
  const response = await fetch(url, {
    method: 'GET',
    headers: { ...getAuthHeadersForMe(), Accept: 'application/json' },
    signal: options.signal,
    cache: 'no-store',
  })
  if (response.status === 401) {
    clearTokensIfUnauthorized()
    return {
      count: 0,
      page: 1,
      page_size: options.pageSize ?? 20,
      results: [],
      unauthorized: true,
    }
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(msg || `me/places/search: ${response.status}`)
  }
  return (await response.json()) as UserPlacesSearchResponse
}

/**
 * Lit un lieu personnel (GET /me/places/:id/).
 */
export async function fetchUserPlaceById(
  id: number,
  options: { signal?: AbortSignal } = {},
): Promise<UserPlaceApiRow | null> {
  const response = await fetch(`${API_URL}/${API_VERSION}/me/places/${id}/`, {
    method: 'GET',
    headers: getAuthHeadersForMe(),
    signal: options.signal,
  })
  if (response.status === 404) return null
  if (response.status === 401) {
    clearTokensIfUnauthorized()
    throw new Error(i18n.t('map:mapApi.session_expiree'))
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(msg || `fetchUserPlaceById: ${response.status}`)
  }
  return (await response.json()) as UserPlaceApiRow
}

/**
 * Crée un lieu personnel (POST /me/places/).
 */
export async function createUserPlace(
  payload: CreateUserPlacePayload,
  options: { signal?: AbortSignal } = {},
): Promise<UserPlaceApiRow> {
  const response = await fetch(`${API_URL}/${API_VERSION}/me/places/`, {
    method: 'POST',
    headers: { ...getAuthHeadersForMe(), 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    signal: options.signal,
  })
  if (response.status === 401) {
    clearTokensIfUnauthorized()
    throw new Error(i18n.t('map:mapApi.session_expiree'))
  }
  if (response.status === 409) {
    const body = await response.json().catch(() => ({}))
    const existingId = (body as { existing_id?: number }).existing_id
    if (existingId != null) {
      const existing = await fetchUserPlaceById(existingId, options)
      if (existing) return existing
    }
    const msg = extractErrorMessage(body) || 'already_saved'
    const err = new Error(msg) as Error & { status?: number; data?: unknown }
    err.status = 409
    err.data = body
    throw err
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(msg || `createUserPlace: ${response.status}`)
  }
  return (await response.json()) as UserPlaceApiRow
}

/**
 * Met à jour un lieu personnel (PATCH /me/places/:id/).
 */
export async function updateUserPlace(
  id: number,
  payload: UpdateUserPlacePayload,
  options: { signal?: AbortSignal } = {},
): Promise<UserPlaceApiRow> {
  const response = await fetch(`${API_URL}/${API_VERSION}/me/places/${id}/`, {
    method: 'PATCH',
    headers: { ...getAuthHeadersForMe(), 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    signal: options.signal,
  })
  if (response.status === 401) {
    clearTokensIfUnauthorized()
    throw new Error(i18n.t('map:mapApi.session_expiree'))
  }
  if (response.status === 404) {
    throw new Error(i18n.t('map:mapApi.lieu_introuvable_ou_acces_refuse'))
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(msg || `updateUserPlace: ${response.status}`)
  }
  return (await response.json()) as UserPlaceApiRow
}

/**
 * Usages d'un lieu dans les itinéraires (GET /me/places/:id/usages/).
 */
export async function fetchUserPlaceUsages(
  id: number,
  options: { signal?: AbortSignal } = {},
): Promise<UserPlaceUsagesResponse> {
  const response = await fetch(`${API_URL}/${API_VERSION}/me/places/${id}/usages/`, {
    method: 'GET',
    headers: getAuthHeadersForMe(),
    signal: options.signal,
  })
  if (response.status === 401) {
    clearTokensIfUnauthorized()
    throw new Error(i18n.t('map:mapApi.session_expiree'))
  }
  if (response.status === 404) {
    throw new Error(i18n.t('map:mapApi.lieu_introuvable_ou_acces_refuse'))
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(msg || `fetchUserPlaceUsages: ${response.status}`)
  }
  return (await response.json()) as UserPlaceUsagesResponse
}

/**
 * Copie un lieu aux nouvelles coordonnées (POST /me/places/:id/copy/).
 */
export async function copyUserPlace(
  id: number,
  payload: CopyUserPlacePayload,
  options: { signal?: AbortSignal } = {},
): Promise<UserPlaceApiRow> {
  const response = await fetch(`${API_URL}/${API_VERSION}/me/places/${id}/copy/`, {
    method: 'POST',
    headers: { ...getAuthHeadersForMe(), 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    signal: options.signal,
  })
  if (response.status === 401) {
    clearTokensIfUnauthorized()
    throw new Error(i18n.t('map:mapApi.session_expiree'))
  }
  if (response.status === 404) {
    throw new Error(i18n.t('map:mapApi.lieu_introuvable_ou_acces_refuse'))
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(msg || `copyUserPlace: ${response.status}`)
  }
  return (await response.json()) as UserPlaceApiRow
}

/**
 * Supprime un lieu personnel (DELETE /me/places/:id/).
 */
export async function deleteUserPlace(id: number, options: { signal?: AbortSignal } = {}): Promise<void> {
  const response = await fetch(`${API_URL}/${API_VERSION}/me/places/${id}/`, {
    method: 'DELETE',
    headers: { ...getAuthHeadersForMe(), Accept: 'application/json' },
    signal: options.signal,
  })
  if (response.status === 401) {
    clearTokensIfUnauthorized()
    throw new Error(i18n.t('map:mapApi.session_expiree'))
  }
  if (response.status === 404) {
    throw new Error(i18n.t('map:mapApi.lieu_introuvable_ou_acces_refuse'))
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(msg || `deleteUserPlace: ${response.status}`)
  }
}

/**
 * Fetches all countries from the geographic hierarchy API.
 */
export async function fetchCountries(options: { signal?: AbortSignal } = {}): Promise<Country[]> {
  const url = `${API_URL}/${API_VERSION}/locations/hierarchy/countries/`
  const response = await fetch(url, { signal: options.signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(
      msg || i18n.t('map:mapApi.failed_to_fetch_countries', { status: response.status, statusText: response.statusText }),
    )
  }

  return response.json()
}

/**
 * Fetches provinces from the geographic hierarchy API, optionally filtered by country.
 */
export async function fetchProvinces(
  options: { country_id?: number; signal?: AbortSignal } = {},
): Promise<Province[]> {
  const url = `${API_URL}/${API_VERSION}/locations/hierarchy/provinces/`
  const params = new URLSearchParams()
  
  if (options.country_id) {
    params.append('country_id', options.country_id.toString())
  }

  const fullUrl = params.toString() ? `${url}?${params.toString()}` : url
  const response = await fetch(fullUrl, { signal: options.signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(
      msg || i18n.t('map:mapApi.failed_to_fetch_provinces', { status: response.status, statusText: response.statusText }),
    )
  }

  return response.json()
}

/**
 * Fetches organizations from the geographic hierarchy API, optionally filtered by country or province.
 */
export async function fetchOrganizations(
  options: { country_id?: number; province_id?: number; signal?: AbortSignal } = {},
): Promise<Organization[]> {
  const url = `${API_URL}/${API_VERSION}/locations/hierarchy/organizations/`
  const params = new URLSearchParams()
  
  if (options.country_id) {
    params.append('country_id', options.country_id.toString())
  }
  if (options.province_id) {
    params.append('province_id', options.province_id.toString())
  }

  const fullUrl = params.toString() ? `${url}?${params.toString()}` : url
  const response = await fetch(fullUrl, { signal: options.signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(
      msg || i18n.t('map:mapApi.failed_to_fetch_organizations', { status: response.status, statusText: response.statusText }),
    )
  }

  return response.json()
}

/**
 * Fetches activities from the geographic hierarchy API, optionally filtered by organization.
 */
export async function fetchActivities(
  options: { organization_id?: number; all?: boolean; signal?: AbortSignal } = {},
): Promise<Activity[]> {
  const url = `${API_URL}/${API_VERSION}/locations/hierarchy/activities/`
  const params = new URLSearchParams()
  
  if (options.organization_id) {
    params.append('organization_id', options.organization_id.toString())
  }
  if (options.all) {
    params.append('all', 'true')
  }

  const fullUrl = params.toString() ? `${url}?${params.toString()}` : url
  const response = await fetch(fullUrl, { signal: options.signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(
      msg || i18n.t('map:mapApi.failed_to_fetch_activities', { status: response.status, statusText: response.statusText }),
    )
  }

  return response.json()
}

/**
 * Type for filter options (refuges, campings, organizations)
 */
export interface FilterOption {
  id: number
  name: string
  slug: string
  icon_color?: string | null
  icon_type?: string | null
  mdi_icon?: string | null
  svg_icon?: string | null
  /** Number of locations (e.g. accommodations) for this type; used in POI panel "Aire de repos". */
  count?: number | null
}

export interface FilterOptionsResponse {
  refuges: FilterOption[]
  campings: FilterOption[]
  organizations: FilterOption[]
  accommodations?: FilterOption[]
  placement_types?: FilterOption[]
  /** Types de POI du filtre « Points d'intérêt » des organisations. */
  organization_poi_types?: FilterOption[]
}

/**
 * Fetches filter options for refuges, campings, and organizations.
 */
export async function fetchFilterOptions(
  options: { signal?: AbortSignal } = {},
): Promise<FilterOptionsResponse> {
  const url = `${API_URL}/${API_VERSION}/locations/filter-options/`
  const response = await fetch(url, { signal: options.signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(
      msg || i18n.t('map:mapApi.failed_to_fetch_filter_options', { status: response.status, statusText: response.statusText }),
    )
  }

  return response.json()
}

/**
 * POI Preset — a named set of POI type slugs that can be activated together on the planning map.
 */
export interface POIPreset {
  id: number
  name: string
  slug: string
  description?: string | null
  icon_color?: string | null
  mdi_icon?: string | null
  display_order: number
  /** Slugs of the POIType objects included in this preset */
  poi_type_slugs: string[]
}

export interface POIPresetsResponse {
  presets: POIPreset[]
}

/**
 * Fetches all active POI presets from the planning API.
 */
export async function fetchPOIPresets(
  options: { signal?: AbortSignal } = {},
): Promise<POIPresetsResponse> {
  const url = `${API_URL}/${API_VERSION}/planning/poi-presets/`
  const response = await fetch(url, { signal: options.signal })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(msg || i18n.t('map:mapApi.failed_to_fetch_poi_presets', { status: response.status, statusText: response.statusText }))
  }
  return response.json()
}

/**
 * Type for POI category with parent-child hierarchy
 */
export interface POICategory {
  id: number
  name: string
  slug: string
  parent_id?: number | null
  icon_color?: string | null
  icon_type?: string | null
  mdi_icon?: string | null
  svg_icon?: string | null
  count?: number | null
  children?: POICategory[]
}

export interface POICategoriesResponse {
  categories: POICategory[]
}

export interface POITypeCountsResponse {
  poi: Record<string, number>
  accommodation: Record<string, number>
  refuge?: Record<string, number>
  camping?: Record<string, number>
  organization?: Record<string, number>
}

/**
 * Fetches POI categories with parent-child hierarchy.
 */
export async function fetchPOICategories(
  options: { signal?: AbortSignal } = {},
): Promise<POICategoriesResponse> {
  const url = `${API_URL}/${API_VERSION}/locations/poi-categories/`
  const response = await fetch(url, { signal: options.signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(
      msg || i18n.t('map:mapApi.failed_to_fetch_poi_categories', { status: response.status, statusText: response.statusText }),
    )
  }

  return response.json()
}

/**
 * Counts location types visible in the current map bounds (count only).
 */
export async function fetchPOITypeCounts(
  bounds: MapBounds,
  options: { signal?: AbortSignal } = {},
): Promise<POITypeCountsResponse> {
  const empty: POITypeCountsResponse = {
    poi: {},
    accommodation: {},
    refuge: {},
    camping: {},
    organization: {},
  }
  const normalizedBounds = normalizeBounds(bounds)
  if (!isValidBounds(normalizedBounds)) {
    return empty
  }

  const params = new URLSearchParams({
    bounds_north: normalizedBounds.north.toString(),
    bounds_south: normalizedBounds.south.toString(),
    bounds_east: normalizedBounds.east.toString(),
    bounds_west: normalizedBounds.west.toString(),
  })
  const url = `${API_URL}/${API_VERSION}/locations/poi-type-counts/?${params.toString()}`
  const response = await fetch(url, { signal: options.signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)
    throw new Error(
      msg || i18n.t('map:mapApi.failed_to_fetch_poi_type', { status: response.status, statusText: response.statusText }),
    )
  }

  const data = (await response.json()) as POITypeCountsResponse
  return {
    poi: data.poi ?? {},
    accommodation: data.accommodation ?? {},
    refuge: data.refuge ?? {},
    camping: data.camping ?? {},
    organization: data.organization ?? {},
  }
}

